import { ChevronsUpDown, type LucideIcon } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/shadcn/popover";
import { Separator } from "@/components/shadcn/separator";
import { Button } from "@/components/shadcn/button";
import { Badge } from "@/components/shadcn/badge";
import { Checkbox } from "@/components/shadcn/checkbox";
import { Label } from "@/components/shadcn/label";

export interface MultiSelectOption {
  id: string;
  label: string;
}

interface Props {
  icon?: LucideIcon;
  label: string;
  emptyText?: string;
  options: MultiSelectOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

export function MultiSelectPopover({
  icon,
  label,
  emptyText,
  options,
  selectedIds,
  onChange,
  disabled,
}: Props) {
  const CustomIcon = icon || ChevronsUpDown;

  const toggle = (checked: boolean, id: string) => {
    onChange(checked ? [...selectedIds, id] : selectedIds.filter((v) => v !== id));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-9 w-full justify-between gap-2" disabled={disabled}>
          <span className="flex items-center gap-2">
            <CustomIcon className="h-4 w-4" />
            <span className="text-xs">{label}</span>
          </span>
          <Badge className="min-w-5 justify-center rounded-sm">{selectedIds.length}</Badge>
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-(--radix-popover-trigger-width) gap-2" align="start">
        {options.length === 0 ? (
          <div className="text-sm font-medium leading-none flex-1 p-1">
            {emptyText || "Keine Einträge vorhanden"}
          </div>
        ) : (
          <div className="max-h-75 overflow-y-auto scrollbar-custom">
            <div className="flex flex-col gap-1 overflow-hidden">
              {options.map((option) => (
                <div
                  key={option.id}
                  className="flex items-center w-full gap-3 p-1.5 rounded transition-colors group select-none"
                >
                  <Checkbox
                    id={`msp-${option.id}`}
                    checked={selectedIds.includes(option.id)}
                    onCheckedChange={(checked) => toggle(checked === true, option.id)}
                    className="cursor-pointer"
                  />
                  <Label htmlFor={`msp-${option.id}`} className="cursor-pointer font-normal text-sm">
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedIds.length > 0 && (
          <>
            <Separator />
            <div className="flex items-center">
              <Button
                variant="destructive"
                className="w-full justify-center text-xs h-8"
                onClick={() => onChange([])}
              >
                Zurücksetzen
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
